import { useContext, useState, useEffect } from "react";
import {
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  TabIndicator,
  Box,
} from "@chakra-ui/react";
import DocumentsTable from "../DocumentTable";
import { AppContext } from "AppProvider/AppContext";
import Exception from "./component/ExceptionTable";
import { useSearchParams } from "react-router-dom";
import SingleSchemaDocument from "../SingleSchemaDocument";
import ProcessLevelCards from "./component/ProcessLevelCards";

export default function Approvals(): JSX.Element {
  const { accountDetails, workflowProcess } = useContext(AppContext);
  const isLevel1 = ["admin", "level-1"].includes(accountDetails?.level ?? "");

  // Decide if "Pending To Submit" tab should be visible
  const showPendingToSubmit = isLevel1 && workflowProcess?.processName !== "foreign";

  // Decide if "All Documents" tab should be visible (ONLY for emerging process)
  const showAllDocuments = workflowProcess?.processName === "marcan";

  const [searchParams] = useSearchParams();
  const documentId = searchParams.get("documentId");

  // Tab value → logical index mapping
  // 3 = Pending To Submit    (only when shown)
  // 0 = Pending
  // 1 = Approved / Uploaded
  // 2 = Rejected
  // 4 = All Documents        (only when shown)
  const tabMapping = [
    ...(showPendingToSubmit ? [3] : []),
    0,
    1,
    2,
    ...(showAllDocuments ? [4] : []),
  ];

  const defaultTab = showPendingToSubmit ? 3 : 0;

  const [selectedTab, setSelectedTab] = useState<number>(defaultTab);

  // Restore from localStorage on mount / when visibility changes
  useEffect(() => {
    const saved = localStorage.getItem("documentTab");
    const savedNum = Number(saved);

    if (saved !== null && !Number.isNaN(savedNum) && tabMapping.includes(savedNum)) {
      setSelectedTab(savedNum);
    } else {
      setSelectedTab(defaultTab);
    }
  }, [showPendingToSubmit, tabMapping, defaultTab]);

  // When ?documentId=xxx is present → force Pending tab (logical 0)
  useEffect(() => {
    if (documentId) {
      setSelectedTab(0);
    }
  }, [documentId]);

  const handleTabChange = (index: number) => {
    const tabValue = tabMapping[index];
    setSelectedTab(tabValue);
    localStorage.setItem("documentTab", String(tabValue));
  };

  // Which physical tab index should be selected
  const selectedIndex = tabMapping.indexOf(selectedTab);

  const tabStyles = {
    _hover: { color: "teal.400", bg: "gray.100" },
    _selected: { color: "teal.400" },
    fontWeight: "700",
    fontSize: { base: "1rem", md: "1.2rem" },
    cursor: "pointer",
    color: "teal.300",
  };

  // ──────────────────────────────────────────────────────────────
  //  Single document view when ?documentId=... is present
  // ──────────────────────────────────────────────────────────────
  if (documentId) {
    return (
      <SingleSchemaDocument
        type="pending"
        isDisabled={false}
        documentId={documentId}
        activePage="Pending"
        name="Pending Approvals"
        isManual={false}
      />
    );
  }

  // ──────────────────────────────────────────────────────────────
  //  Normal tabs view
  // ──────────────────────────────────────────────────────────────
  return (
    <Box p={4}>
      {accountDetails?.level && accountDetails?.level !== "admin" && Number(accountDetails?.level?.split('-')[1]) > 3 && <ProcessLevelCards />}
      <Tabs
        variant="unstyled"
        position="relative"
        onChange={handleTabChange}
        index={selectedIndex}
      >
        <TabList>
          {showPendingToSubmit && <Tab {...tabStyles}>Pending To Submit</Tab>}
          <Tab {...tabStyles}>Pending</Tab>
          <Tab {...tabStyles}>
            {accountDetails?.role === "uploader" ? "Uploaded" : "Approved"}
          </Tab>
          <Tab {...tabStyles}>Rejected</Tab>
          {showAllDocuments && <Tab {...tabStyles}>All Documents</Tab>}
        </TabList>

        <TabIndicator mt="-1.5px" height="2px" bg="teal.400" borderRadius="1px" />

        <TabPanels>
          {/* Pending To Submit – only exists when shown */}
          {showPendingToSubmit && (
            <TabPanel p={0}>
              {selectedTab === 3 && (
                <Exception
                  name="Pending Approval"
                  type="pending"
                  activePage="Pending Approval"
                  isDisabled={false}
                  isManual={true}
                />
              )}
            </TabPanel>
          )}

          {/* Pending */}
          <TabPanel p={0}>
            {selectedTab === 0 && (
              <DocumentsTable
                name="Pending Approval"
                type="pending"
                activePage="Pending Approval"
                isDisabled={false}
                isManual={true}
              />
            )}
          </TabPanel>

          {/* Approved / Uploaded */}
          <TabPanel p={0}>
            {selectedTab === 1 && (
              <DocumentsTable
                name="Approved"
                type="approved"
                activePage="Approved"
                isDisabled={true}
                isManual={true}
              />
            )}
          </TabPanel>

          {/* Rejected */}
          <TabPanel p={0}>
            {selectedTab === 2 && (
              <DocumentsTable
                name="Rejected"
                type="rejected"
                activePage="Rejected"
                isDisabled={true}
                isManual={true}
              />
            )}
          </TabPanel>

          {/* All Documents */}
          {showAllDocuments && (
            <TabPanel p={0}>
              {selectedTab === 4 && (
                <DocumentsTable
                  name="All Documents"
                  type="all"
                  activePage="All Documents"
                  isDisabled={true}
                  isManual={true}
                />
              )}
            </TabPanel>
          )}
        </TabPanels>
      </Tabs>
    </Box>
  );
}